import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { useLocalization } from '@/Hooks';
import { useDispatch } from 'react-redux';
import { mandatSettingsActions } from '@/Pages/Settings/modules/MandatsSettings/services/reducer';

export function useMandatSettings() {
  const {
    tours: {
      MandatsSettings: { Default, LegalNotice },
    },
  } = useLocalization();

  const dispatch = useDispatch();

  const pageTour = useMemo<TourType>(
    () => ({
      id: 'pageTour',
      title: Default.Title,
      steps: [
        {
          content: Default.Step1.Text,
          placement: 'auto',
          target: '#stepTitle',
          title: Default.Step1.Title,
          disableBeacon: true,
          link: Default.Step1.DetailsLink,
        },
        {
          content: Default.Step2.Text,
          placement: 'auto',
          target: '#mandatsTable',
          title: Default.Step2.Title,
          disableBeacon: true,
          link: Default.Step2.DetailsLink,
        },
        {
          content: Default.Step3.Text,
          placement: 'auto',
          target: '#crudExtended',
          title: Default.Step3.Title,
          disableBeacon: true,
          link: Default.Step3.DetailsLink,
        },
        {
          content: Default.Step4.Text,
          placement: 'auto',
          target: '.dx-toolbar-after',
          title: Default.Step4.Title,
          disableBeacon: true,
          link: Default.Step4.DetailsLink,
          spotlightPadding: 0,
          styles: {
            spotlight: {
              maxWidth: '500px',
            },
          },
        },
        {
          content: Default.Step5.Text,
          placement: 'auto',
          target: '#tableImportButton',
          title: Default.Step5.Title,
          disableBeacon: true,
          link: Default.Step5.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(0)),
          styles: {
            spotlight: {
              minWidth: '65px',
            },
          },
        },
        {
          content: Default.Step6.Text,
          placement: 'auto',
          target: '#mandatsFormBlock',
          title: Default.Step6.Title,
          disableBeacon: true,
          link: Default.Step6.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(0)),
        },
        {
          content: Default.Step7.Text,
          placement: 'auto',
          target: '#mandatInfo',
          title: Default.Step7.Title,
          disableBeacon: true,
          link: Default.Step7.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(0)),
        },
        {
          content: Default.Step8.Text,
          placement: 'auto',
          target: '#legalNoticeStep',
          title: Default.Step8.Title,
          disableBeacon: true,
          link: Default.Step8.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(1)),
        },
        {
          content: Default.Step9.Text,
          placement: 'auto',
          target: '#addressInfo',
          title: Default.Step9.Title,
          disableBeacon: true,
          link: Default.Step9.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(1)),
        },
        {
          content: Default.Step10.Text,
          placement: 'auto',
          target: '#vatRatesInfo',
          title: Default.Step10.Title,
          disableBeacon: true,
          link: Default.Step10.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(1)),
        },
        {
          content: Default.Step11.Text,
          placement: 'auto',
          target: '#bankAccountInfo',
          title: Default.Step11.Title,
          disableBeacon: true,
          link: Default.Step11.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(1)),
        },
        {
          content: Default.Step12.Text,
          placement: 'auto',
          target: '#logoInfo',
          title: Default.Step12.Title,
          disableBeacon: true,
          link: Default.Step12.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(1)),
        },
        {
          content: Default.Step13.Text,
          placement: 'auto',
          target: '#documentsInfo',
          title: Default.Step13.Title,
          disableBeacon: true,
          link: Default.Step13.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(2)),
        },
        {
          content: Default.Step14.Text,
          placement: 'auto',
          target: '#documentsNextNumberInfo',
          title: Default.Step14.Title,
          disableBeacon: true,
          link: Default.Step14.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(2)),
        },
        {
          content: Default.Step15.Text,
          placement: 'auto',
          target: '#emailInfo',
          title: Default.Step15.Title,
          disableBeacon: true,
          link: Default.Step15.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(3)),
        },
        {
          content: Default.Step16.Text,
          placement: 'auto',
          target: '#freeTextInfo',
          title: Default.Step16.Title,
          disableBeacon: true,
          link: Default.Step16.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(4)),
        },
        {
          content: Default.Step17.Text,
          placement: 'auto',
          target: '#navButtons',
          title: Default.Step17.Title,
          disableBeacon: true,
          link: Default.Step17.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setOverviewStep(4)),
        },
      ],
    }),
    [Default],
  );

  const mandatSettingsTour = useMemo<TourType>(
    () => ({
      id: 'mandatSettingsTour',
      title: LegalNotice.Title,
      before: () => {
        dispatch(mandatSettingsActions.setMandatModal({ visible: true }));
      },
      steps: [
        {
          content: LegalNotice.Step1.Text,
          placement: 'auto',
          target: '#modalStepTitle',
          title: LegalNotice.Step1.Title,
          disableBeacon: true,
          link: LegalNotice.Step1.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(0)),
        },
        {
          content: LegalNotice.Step2.Text,
          placement: 'auto',
          target: '#mandatBlock',
          title: LegalNotice.Step2.Title,
          disableBeacon: true,
          link: LegalNotice.Step2.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(0)),
        },
        {
          content: LegalNotice.Step3.Text,
          placement: 'auto',
          target: '#modalNavButtons',
          title: LegalNotice.Step3.Title,
          disableBeacon: true,
          link: LegalNotice.Step3.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(0)),
        },
        {
          content: LegalNotice.Step4.Text,
          placement: 'auto',
          target: '#legalNoticeModalStep',
          title: LegalNotice.Step4.Title,
          disableBeacon: true,
          link: LegalNotice.Step4.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(1)),
        },
        {
          content: LegalNotice.Step5.Text,
          placement: 'auto',
          target: '#invoiceAddressBlock',
          title: LegalNotice.Step5.Title,
          disableBeacon: true,
          link: LegalNotice.Step5.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(1)),
        },
        {
          content: LegalNotice.Step6.Text,
          placement: 'auto',
          target: '#addAddressButton',
          title: LegalNotice.Step6.Title,
          disableBeacon: true,
          link: LegalNotice.Step6.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(1)),
        },
        {
          content: LegalNotice.Step7.Text,
          placement: 'auto',
          target: '#contactsBlock',
          title: LegalNotice.Step7.Title,
          disableBeacon: true,
          link: LegalNotice.Step7.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(1)),
        },
        {
          content: LegalNotice.Step8.Text,
          placement: 'auto',
          target: '#vatRatesBlock',
          title: LegalNotice.Step8.Title,
          disableBeacon: true,
          link: LegalNotice.Step8.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(1)),
        },
        {
          content: LegalNotice.Step9.Text,
          placement: 'auto',
          target: '#bankAccountBlock',
          title: LegalNotice.Step9.Title,
          disableBeacon: true,
          link: LegalNotice.Step9.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(1)),
        },
        {
          content: LegalNotice.Step10.Text,
          placement: 'auto',
          target: '#logoBLock',
          title: LegalNotice.Step10.Title,
          disableBeacon: true,
          link: LegalNotice.Step10.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(1)),
        },
        {
          content: LegalNotice.Step11.Text,
          placement: 'auto',
          target: '#documentsBlock',
          title: LegalNotice.Step11.Title,
          disableBeacon: true,
          link: LegalNotice.Step11.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(2)),
        },
        {
          content: LegalNotice.Step12.Text,
          placement: 'auto',
          target: '#emailBlock',
          title: LegalNotice.Step12.Title,
          disableBeacon: true,
          link: LegalNotice.Step12.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(3)),
        },
        {
          content: LegalNotice.Step13.Text,
          placement: 'auto',
          target: '#freeTextBlock',
          title: LegalNotice.Step13.Title,
          disableBeacon: true,
          link: LegalNotice.Step13.DetailsLink,
          callback: () => dispatch(mandatSettingsActions.setModalStep(4)),
        },
      ],
    }),
    [LegalNotice],
  );

  return [pageTour, mandatSettingsTour];
}
